<template>
  <div class="full-width d-flex flex-column align-start">
    <v-data-table
      :headers="columns"
      :hide-default-header="false"
      :hide-default-footer="!loading.payments && table.total < 10"
      :items="payments"
      :item-key="itemKey"
      :items-per-page="table.itemsPerPage"
      :footer-props="table.footerProps"
      :options.sync="table.options"
      :server-items-length="table.total"
      :loading="loading.payments"
      must-sort
      :sort-desc="true"
      sort-by="description"
      class="full-width payment-table"
      :class="[`elevation-${elevation}`, elevation == 0 ? 'light-grey-border' : '']"
      :item-class="
        () => {
          return isParentLevel ? 'cursor-pointer' : '';
        }
      "
      @click:row="loadTransactionDrawer"
    >
      <template v-slot:[`header.description`]="{ header }">
        <Typography variant="subtitle-2" as="span">
          {{ header.text }}
        </Typography>
      </template>
      <template v-slot:[`header.amount`]="{ header }">
        <Typography variant="subtitle-2" as="span">
          {{ header.text }}
        </Typography>
      </template>
      <template v-slot:no-data>
        <Typography variant="body-2" class="black--text">No rewards found</Typography>
      </template>
      <template v-slot:no-results>
        <Typography variant="body-2" class="black--text">No rewards found</Typography>
      </template>
      <template v-slot:loading>
        <Typography variant="body-2" class="black--text">Loading... Please wait</Typography>
      </template>
      <template v-slot:[`item.amount`]="{ item }">
        <Typography variant="body-2" class="success--text">{{
          formatCurrency(
            item.amount,
            item.ParentPaymentRule?.Budget?.Award || item.Budget?.Award,
            true
          )
        }}</Typography>
      </template>
      <template v-slot:[`item.description`]="{ item }">
        <div class="d-flex flex-column word-break py-4">
          <Typography class="mb-1 merchant-name" variant="body-2" as="span">
            {{
              isParentLevel
                ? item.titleSummary || "Payment"
                : item?.ParentPaymentRule?.description || "Payment"
            }}
          </Typography>
          <Typography variant="body-2" as="span">
            {{ item.formattedDate }}
          </Typography>
        </div>
      </template>
    </v-data-table>
    <v-navigation-drawer v-model="display.payment" temporary fixed right width="500">
      <div v-if="display.payment">
        <v-toolbar dark color="brandDarkCyan" rounded="0">
          <v-btn dark icon large @click="closeTransactionDrawer" class="ml-1">
            <v-icon>mdi-close-circle</v-icon>
          </v-btn>
          <v-toolbar-title class="d-flex align-center font-weight-bold"
            >Reward details</v-toolbar-title
          >
        </v-toolbar>
        <div class="text-left pa-4 word-break">
          <h2>{{ display.paymentHeader }}</h2>
          <p>
            {{ display.date
            }}<span v-if="hasPermissionToViewClients()"> (ID: {{ display.id }})</span>
          </p>
          <p v-if="display.frequency != null && display.frequency != 'ONE_TIME'">
            {{
              display.frequency == "MONTHLY"
                ? "Montly payment"
                : display.frequency == "WEEKLY"
                ? "Weekly payment"
                : "Daily payment"
            }}
            <span v-if="formattedStartDate"> from {{ formattedStartDate }}</span>
            <span v-if="formattedEndDate"> to {{ formattedEndDate }}</span>
          </p>
          <p class="font-italic" v-if="formattedDescription">"{{ formattedDescription }}"</p>
          <v-divider />
          <v-data-table
            :headers="paymentRuleColumns"
            :hide-default-header="false"
            :hide-default-footer="false"
            mobile-breakpoint="0"
            :items="rules"
            item-key="paymentRuleId"
            :items-per-page="ruleTable.itemsPerPage"
            :footer-props="ruleTable.footerProps"
            :options.sync="ruleTable.options"
            :server-items-length="ruleTable.total"
            :loading="loading.payment"
            must-sort
            :sort-desc="true"
            sort-by="displayName"
            class="full-width payment-table"
          >
            <template v-slot:[`header.displayName`]="{ header }">
              <Typography variant="subtitle-2" as="span">
                {{ header.text }}
              </Typography>
            </template>
            <template v-slot:[`header.amount`]="{ header }">
              <Typography variant="subtitle-2" as="span">
                {{ header.text }}
              </Typography>
            </template>
            <template v-slot:no-data>
              <Typography variant="body-2" class="black--text">No payments found</Typography>
            </template>
            <template v-slot:no-results>
              <Typography variant="body-2" class="black--text">No payments found</Typography>
            </template>
            <template v-slot:loading>
              <Typography variant="body-2" class="black--text">Loading... Please wait</Typography>
            </template>
            <template v-slot:[`item.amount`]="{ item }">
              <Typography variant="body-2" as="span">{{
                formatCurrency(item.amount, display.Award, true)
              }}</Typography>
            </template>
            <template v-slot:[`item.displayName`]="{ item }">
              <div class="d-flex flex-column word-break my-1">
                <Typography variant="body-2" as="span">{{
                  item.User ? item.User.displayName : "Unknown user"
                }}</Typography>
                <Typography variant="body-2" as="span">{{ item.formattedDate }}</Typography>
              </div>
            </template>
          </v-data-table>
        </div>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script>
// import MarqetaService from "@/services/MarqetaService";
import WalletService from "@/services/WalletService";
import Typography from "@/components/generic/Typography.vue";
import formatCurrencyMixin from "@/mixins/formatCurrency";
import { debounce } from "@/shared_data/functions";
import moment from "moment";
import { mapState, mapGetters } from "vuex";

export default {
  name: "RewardsSentLog",
  props: {
    // Used to know if we fetch and show parent payment rules or the payment rule level
    level: {
      type: String,
      default: "ParentPaymentRule",
    },
    // Filter payments for a certain user
    byUser: String,
    clientId: Number,
    elevation: {
      type: Number,
      default: 2,
    },
    search: String,
    adminView: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Typography,
  },
  mixins: [formatCurrencyMixin],
  data() {
    return {
      loading: {
        balance: false,
        payments: false,
        payment: false,
      },
      display: {
        admin: false,
        payment: false,
        id: null,
        parentPaymentRuleId: null,
        paymentHeader: null,
        date: null,
        description: null,
        frequency: null,
        startDate: null,
        endDate: null,
      },
      data: {
        payments: [],
        rules: [],
      },
      parentColumns: [
        {
          text: "Description    ",
          value: "description",
          width: "50%",
          sortable: true,
          align: "start",
        },
        {
          text: "    Amount",
          value: "amount",
          width: "15%",
          align: "end",
          sortable: true,
        },
      ],
      paymentRuleColumns: [
        {
          text: "Name  ",
          value: "displayName",
          width: "50%",
          sortable: true,
          align: "start",
        },
        {
          text: "       Amount",
          value: "amount",
          width: "20%",
          align: "end",
          sortable: true,
        },
      ],
      table: {
        itemsPerPage: 10,
        page: 1,
        search: null,
        total: 0,
        options: {},
        footerProps: {
          "items-per-page-options": [10, 25, 50, 100],
        },
        latestFilter: null,
        latestSort: null,
      },
      ruleTable: {
        itemsPerPage: 10,
        page: 1,
        search: null,
        total: 0,
        options: {},
        footerProps: {
          "items-per-page-options": [10, 25, 50, 100],
        },
        latestFilter: null,
        latestSort: null,
      },
    };
  },
  created() {
    // this.getParentPaymentRules();
  },
  mounted() {},
  beforeUnmount() {},
  methods: {
    async getParentPaymentRules(reset = false) {
      try {
        console.log("Getting parent rules ", { reset });
        this.loading.payments = true;
        if (reset) this.table.options.page = 1;
        let options = this.table.options;
        let page = options.page || 1;
        let limit = options.itemsPerPage;
        let offset = reset ? 0 : (page - 1) * limit;
        let filter = `status != 'Deleted' && status != 'Failed'`;
        if (!this.adminView) filter += ` && createdBy = '${this.userProfile.userId}'`;
        if (!this.hasPermissionToViewClients())
          filter += ` && clientId = '${this.userProfile.clientId}'`;
        let search =
          this.table.search || this.search
            ? (this.table.search || this.search).replace(/'/g, "\\'")
            : null;
        if (search)
          filter += ` && (description.like('%${search}%') || titleSummary.like('%${search}%') || amount.like('%${search}%') || parentPaymentRuleId = '${search}')`;
        let sort = "startDate DESC";
        if (options.sortBy && options.sortBy.length) {
          let col = options.sortBy;
          let type =
            options.sortDesc && options.sortDesc.length && options.sortDesc[0] === true
              ? "DESC"
              : "ASC";
          if (col == "description") col = "startDate";
          sort = `${col} ${type}`;
        }
        this.table.latestSort = sort;
        this.table.latestFilter = filter;
        let params = {
          filter,
          offset,
          limit,
          sort,
          expand: "Budget.Award",
        };
        console.log(params);
        let results = await WalletService.getParentPaymentRulesV2(params, this.magicLinkToken);

        if (this.table.latestFilter != filter || this.table.latestSort != sort) {
          console.log("Returning early due to change");
          return;
        }
        console.log("Got payments sent ", results.result.rows);
        this.data.payments = results.result.rows.map((x) => {
          x.formattedDate = moment(x.startDate).local().format("MMM D, YYYY");
          return x;
        });

        this.table.total = results.result.count;
      } catch (err) {
        console.log("Error with parent payment rules ", err);
      } finally {
        this.loading.payments = false;
      }
    },
    async getPaymentRules(reset = false) {
      try {
        // We shouldn't hit this state
        if (!this.isParentLevel && !this.byUser) return;
        console.log("Getting payment rules ", { reset });
        this.loading.payments = true;
        if (reset) this.table.options.page = 1;
        let options = this.table.options;
        let page = options.page || 1;
        let limit = options.itemsPerPage;
        let offset = reset ? 0 : (page - 1) * limit;
        let filter = `status != 'Deleted' && ParentPaymentRule.status != 'Deleted' && ParentPaymentRule.status != 'Failed' && userId = '${this.byUser}'`;
        if (!this.hasPermissionToViewClients())
          filter += ` && clientId = '${this.userProfile.clientId}'`;
        else if (this.clientId) filter += ` && clientId = ${this.clientId}`;
        let sort = "startDate DESC";
        if (options.sortBy && options.sortBy.length) {
          let col = options.sortBy;
          let type =
            options.sortDesc && options.sortDesc.length && options.sortDesc[0] === true
              ? "DESC"
              : "ASC";
          if (col == "description") col = "startDate";
          sort = `${col} ${type}`;
        }
        this.table.latestSort = sort;
        this.table.latestFilter = filter;
        let params = {
          filter,
          offset,
          limit,
          expand: "ParentPaymentRule.Budget.Award",
          sort,
        };
        console.log(params);
        let results = await WalletService.getPaymentRulesV2(params, this.magicLinkToken);

        if (this.table.latestFilter != filter || this.table.latestSort != sort) {
          console.log("Returning early due to change");
          return;
        }
        console.log("Got user payments ", results.result.rows);
        this.data.payments = results.result.rows.map((x) => {
          x.formattedDate = moment(x.startDate).local().format("MMM D, YYYY");
          return x;
        });

        this.table.total = results.result.count;
      } catch (err) {
        console.log("Error with payment rules ", err);
      } finally {
        this.loading.payments = false;
      }
    },
    async getPaymentRulesForId(reset = false) {
      try {
        console.log("Getting payment rules ", { reset });
        this.loading.payment = true;
        if (reset) this.ruleTable.options.page = 1;
        let options = this.ruleTable.options;
        let page = options.page || 1;
        let limit = options.itemsPerPage;
        let offset = reset ? 0 : (page - 1) * limit;
        let filter = `status != 'Deleted' && parentPaymentRuleId = ${this.display.id}`;
        console.log(options.sortBy);
        let sort = "startDate DESC";
        if (options.sortBy && options.sortBy.length) {
          let col = options.sortBy;
          let type =
            options.sortDesc && options.sortDesc.length && options.sortDesc[0] === true
              ? "DESC"
              : "ASC";
          if (col == "displayName") col = "startDate";
          else sort = `${col} ${type}`;
        }
        this.ruleTable.latestSort = sort;
        this.ruleTable.latestFilter = filter;
        let params = {
          filter,
          offset,
          limit,
          expand: "User",
          sort,
        };
        console.log(params);
        let results = await WalletService.getPaymentRulesV2(params, this.magicLinkToken);

        if (this.ruleTable.latestFilter != filter || this.ruleTable.latestSort != sort) {
          console.log("Returning early due to change");
          return;
        }
        console.log("Got user payments ", results.result.rows);
        this.data.rules = results.result.rows.map((x) => {
          x.formattedDate = moment(x.startDate).local().format("MMM D, YYYY");
          return x;
        });

        this.ruleTable.total = results.result.count;
      } catch (err) {
        console.log("Error with payment rules ", err);
      } finally {
        this.loading.payment = false;
      }
    },
    loadTransactionDrawer(row) {
      // No clicking if lower level
      if (!this.isParentLevel) return;
      this.data.rules = [];
      this.ruleTable.options = { page: 1 };
      this.display.payment = true;
      this.display.parentPaymentRuleId = row.parentPaymentRuleId || row;
      this.display.paymentHeader = row.titleSummary;
      this.display.date = row.formattedDate;
      this.display.id = row.parentPaymentRuleId;
      this.display.description = row.description;
      this.display.frequency = row.frequency;
      this.display.startDate = row.startDate;
      this.display.endDate = row.endDate;
      this.display.Award = row.Budget?.Award;
    },
    closeTransactionDrawer() {
      this.data.rules = [];
      this.ruleTable.options = { page: 1 };
      this.display.payment = false;
      this.display.parentPaymentRuleId = null;
      this.display.paymentHeader = null;
      this.display.date = null;
      this.display.description = null;
      this.display.id = null;
      this.display.frequency = null;
      this.display.startDate = null;
      this.display.endDate = null;
      this.display.Award = null;
    },
  },
  computed: {
    ...mapState(["userProfile", "balances", "cashBalance", "confettiPlayed", "magicLinkToken"]),
    ...mapGetters(["hasPermissionToViewClients"]),
    isMobile() {
      return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm;
    },
    isParentLevel() {
      return this.level === "ParentPaymentRule";
    },
    columns() {
      let col = this.parentColumns;
      // if (this.isMobile) col[0].align = "end";
      // else col[0].align = "start";
      // else if (this.isMobile) return col.filter(x => x.mobile !== false);
      return col;
    },
    payments() {
      if (this.loading.payments) return [];

      return this.data.payments;
    },
    rules() {
      if (this.loading.payment) return [];

      return this.data.rules;
    },
    searchValue() {
      // if (this.search)
      return this.search;
      // return this.search.transactions;
    },
    formattedStartDate() {
      if (!this.display.startDate) return null;
      return moment(this.display.startDate).format("MMM D, YYYY");
    },
    formattedEndDate() {
      if (!this.display.endDate) return null;
      return moment(this.display.endDate).format("MMM D, YYYY");
    },
    formattedDescription() {
      if (!this.display.description || typeof this.display.description !== "string") return null;
      return this.display.description.trim();
    },
    itemKey() {
      return this.isParentLevel ? "parentPaymentRuleId" : "paymentRuleId";
    },
  },
  watch: {
    search: debounce(function (newVal) {
      this.table.search = newVal;
      this.getParentPaymentRules(true);
    }, 500),
    "table.options": {
      handler(newVal, oldVal) {
        // Used to indicate if we wipe the table and start with an empty array, versus pushing more data on
        var reset = false;
        // var apiCall = true;
        // If none of these exist then we're probably loading the page for the first time so we reset
        if (!oldVal.itemsPerPage || !oldVal.page || !oldVal.sortBy || !oldVal.sortDesc)
          reset = true;
        else {
          // If any values changed from their old to new states, then we reset because they're changing a sort
          if (
            newVal.itemsPerPage !== oldVal.itemsPerPage ||
            (newVal.sortBy && oldVal.sortBy && newVal.sortBy[0] !== oldVal.sortBy[0]) ||
            (newVal.sortDesc && oldVal.sortDesc && newVal.sortDesc[0] !== oldVal.sortDesc[0])
          )
            reset = true;
        }

        // if (apiCall)
        if (this.isParentLevel) this.getParentPaymentRules(reset, "watcher");
        else this.getPaymentRules(reset, "table watcher");
      },
      deep: true,
    },
    "ruleTable.options": {
      handler(newVal, oldVal) {
        console.log("New options ", newVal);
        console.log("Old options ", oldVal);
        // Used to indicate if we wipe the table and start with an empty array, versus pushing more data on
        var reset = false;
        // var apiCall = true;
        // If none of these exist then we're probably loading the page for the first time so we reset
        if (!oldVal.itemsPerPage || !oldVal.page || !oldVal.sortBy || !oldVal.sortDesc)
          reset = true;
        else {
          // If any values changed from their old to new states, then we reset because they're changing a sort
          if (
            newVal.itemsPerPage !== oldVal.itemsPerPage ||
            (newVal.sortBy && oldVal.sortBy && newVal.sortBy[0] !== oldVal.sortBy[0]) ||
            (newVal.sortDesc && oldVal.sortDesc && newVal.sortDesc[0] !== oldVal.sortDesc[0])
          )
            reset = true;
        }

        this.getPaymentRulesForId(reset, "watcher");
      },
      deep: true,
    },
    adminView: {
      handler(newVal, oldVal) {
        console.log("Admin view new val ", newVal);
        console.log("Admin view old val ", oldVal);

        this.getParentPaymentRules(true, "adminView watcher");
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
/* Removes header on mobile to sort columns */
.payment-table-mobile >>> .v-data-table__wrapper > table > thead {
  display: none;
}

/* Make footer page count text larger */
::v-deep .v-data-footer {
  font-size: 14px;
}

/* Truncate description name to 2 lines */
.description-name {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2; /* Number of lines to show */
  overflow: hidden;
  text-overflow: ellipsis; /* Adds the ellipsis (...) */
}
</style>
